import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown, AlertTriangle, Check, X, Clock, Info } from 'lucide-react';
import {
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, ReferenceArea,
} from 'recharts';
import {
  getMssSquad, getMssPlayer, getMssHistory, getMssReviewQueue, decideMssReview,
  getMssDrillClasses, setMssDrillClass, getPlayers,
} from '../../api';

// Maximal Sprinting Speed. Replaces "highest value ever recorded" with a screened,
// bounded-window estimate. Two outputs that must never be conflated:
//   mss_ceiling  — an ACCURACY statistic, sets speed zones and sprint prescription.
//                  Screened maximum over a bounded window, drawn from the dedicated
//                  drill AND matches/11v11 alike.
//   session_vmax — a PRECISION statistic, the weekly trend only. MAXSPEED sessions
//                  only, and never used to set a threshold.
// See docs/mss-methodology.md.

const CLASSES = ['MAXSPEED', 'ELIGIBLE', 'SESSION_ONLY', 'INELIGIBLE', 'UNCLASSIFIED'];

const CONFIDENCE_STYLE = {
  high:     { bg: '#16653420', fg: '#22c55e', label: 'High' },
  moderate: { bg: '#92400e20', fg: '#f59e0b', label: 'Moderate' },
  low:      { bg: '#7f1d1d20', fg: '#ef4444', label: 'Low' },
  stale:    { bg: '#37415120', fg: '#9ca3af', label: 'Stale' },
};

const CONFIDENCE_RULES = [
  'high — enough observations, and at least 2 from the dedicated max-speed drill.',
  'moderate — enough observations, but fewer than 2 from the dedicated drill. A ceiling built from competition alone is real but unstandardised.',
  'low — fewer than 4 eligible observations even after widening to the 12-week fallback window.',
  'stale — no eligible observation for over 21 days, or an absence spanning the window. A returning player’s pre-injury ceiling is never handed out as a basis for prescribing sprint exposure.',
];

const REJECT_REASONS = ['artefact', 'wrong_player_tag', 'collision', 'unknown'];

const FLAG_LABEL = {
  gap_screen: 'Implausible jump from reference set',
  uncorroborated: 'Awaiting corroboration',
  declining_review: 'Ceiling declining',
  eligible_above_ceiling: 'Above ceiling, eligible format',
  ineligible_above_ceiling: 'Above ceiling, ineligible format',
  unexplained_session_peak: 'Session peak no drill accounts for',
};

const fmt = (v, d = 1) => (v === null || v === undefined || isNaN(v) ? '—' : Number(v).toFixed(d));

// Same NULL-to-bottom-regardless-of-direction comparator as LineupBuilder.js /
// TeamKpiOverview.js / Team360Table.js (Stage 4) — not Availability.js's, which only
// sorts nulls last when descending. Each sortable table in this codebase keeps its own
// copy (existing convention).
function compareRows(a, b, key, dir) {
  const av = a[key], bv = b[key];
  const aNull = av === null || av === undefined;
  const bNull = bv === null || bv === undefined;
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;
  if (typeof av === 'string') return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  return dir === 'asc' ? av - bv : bv - av;
}

function SortHeader({ label, sortKey, activeKey, dir, onClick, align }) {
  const active = sortKey === activeKey;
  const Icon = !active ? ArrowUpDown : dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th onClick={() => onClick(sortKey)}
      style={{ textAlign: align || 'left', padding: '8px 14px', color: active ? 'var(--text-primary)' : 'var(--text-muted)', fontWeight: 700, borderBottom: '1px solid var(--border)', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', fontSize: '0.78rem' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, justifyContent: align === 'right' ? 'flex-end' : 'flex-start' }}>{label} <Icon size={11} /></span>
    </th>
  );
}

// Every MSS number in the app renders with this. A bare number with no confidence
// indicator is a bug.
function ConfidenceBadge({ confidence, nObs }) {
  const s = CONFIDENCE_STYLE[confidence] || CONFIDENCE_STYLE.stale;
  return (
    <span title={CONFIDENCE_RULES.join('\n')}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: s.bg, color: s.fg, borderRadius: 6, padding: '2px 8px', fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
      {s.label}{nObs != null && <span style={{ opacity: 0.75, fontWeight: 500 }}>n={nObs}</span>}
    </span>
  );
}

function MssValue({ value, confidence, nObs, size = '1.05rem' }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontSize: size, fontWeight: 700, color: 'var(--text-primary)' }}>{fmt(value)} km/h</span>
      <ConfidenceBadge confidence={confidence} nObs={nObs} />
    </span>
  );
}

function Note({ children, tone = 'muted' }) {
  const bg = tone === 'warn' ? '#92400e18' : 'var(--bg-elevated, #ffffff08)';
  const fg = tone === 'warn' ? '#f59e0b' : 'var(--text-muted)';
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: bg, border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', fontSize: '0.78rem', color: fg, lineHeight: 1.5 }}>
      <Info size={14} style={{ flexShrink: 0, marginTop: 2 }} />
      <div>{children}</div>
    </div>
  );
}

const cardStyle = { background: 'var(--bg-card, #ffffff06)', border: '1px solid var(--border)', borderRadius: 12, padding: 18 };
const tdStyle = { padding: '9px 14px', borderBottom: '1px solid var(--border)', fontSize: '0.82rem', color: 'var(--text-secondary)' };
const btn = (active) => ({ padding: '0 18px', height: 34, border: 'none', fontFamily: 'inherit', cursor: 'pointer', background: active ? '#2563eb' : 'transparent', color: active ? '#fff' : 'var(--text-secondary)', fontSize: '0.82rem', fontWeight: active ? 600 : 400 });

export default function MSS({ user = null }) {
  const [view, setView] = useState('squad');
  const [squad, setSquad] = useState([]);
  const [players, setPlayers] = useState([]);
  const [queue, setQueue] = useState([]);
  const [classes, setClasses] = useState([]);
  const [playerId, setPlayerId] = useState('');
  const [detail, setDetail] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState('mss_ceiling_kmh');
  const [sortDir, setSortDir] = useState('desc');

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p, q, c] = await Promise.all([
        getMssSquad(), getPlayers(), getMssReviewQueue(), getMssDrillClasses(),
      ]);
      setSquad(s || []); setPlayers(p || []); setQueue(q || []); setClasses(c || []);
      if (!playerId && s && s.length) setPlayerId(String(s[0].player_id));
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [playerId]);

  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!playerId) return;
    Promise.all([getMssPlayer(playerId), getMssHistory(playerId)])
      .then(([d, h]) => { setDetail(d); setHistory(h); })
      .catch(e => console.error(e));
  }, [playerId]);

  const toggleSort = (k) => {
    if (k === sortKey) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(k); setSortDir('desc'); }
  };

  const sortedSquad = useMemo(
    () => [...squad].sort((a, b) => compareRows(a, b, sortKey, sortDir)),
    [squad, sortKey, sortDir]);

  const openCount = queue.filter(q => !q.decision).length;

  return (
    <div style={{ maxWidth: 1180, margin: '0 auto', paddingBottom: 48 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Maximal Sprinting Speed</h2>
          <p style={{ fontSize: '0.83rem', color: 'var(--text-muted)', margin: '4px 0 0' }}>
            Screened, bounded-window speed ceiling — replaces the all-time maximum
          </p>
        </div>
        <div style={{ display: 'flex', borderRadius: 9, border: '1px solid var(--border-strong)', overflow: 'hidden', height: 36, flexShrink: 0 }}>
          {[['squad', 'Squad'], ['player', 'Player'], ['review', `Review${openCount ? ` (${openCount})` : ''}`], ['classes', 'Drill classes']].map(([v, label]) => (
            <button key={v} onClick={() => setView(v)} style={btn(view === v)}>{label}</button>
          ))}
        </div>
      </div>

      {loading && <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Loading…</p>}

      {/* ══════════════ SQUAD ══════════════ */}
      {view === 'squad' && !loading && (
        <div style={{ display: 'grid', gap: 16 }}>
          <Note>
            The ceiling draws on the dedicated max-speed drill <strong>and</strong> matches and
            11v11 alike — a low match value is uninformative and can never drag a maximum down,
            so restricting to the drill would systematically underestimate, worst of all for the
            players who sprint hardest in competition. No trend arrows are shown: without a
            per-session record of whether a maximal effort was attempted, a fall in the weekly
            number cannot be told apart from a session run to prescription.
          </Note>
          <div style={{ ...cardStyle, padding: 0, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
              <thead>
                <tr>
                  <SortHeader label="Player" sortKey="player_name" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
                  <SortHeader label="Pos" sortKey="position" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
                  <SortHeader label="MSS" sortKey="mss_ceiling_kmh" activeKey={sortKey} dir={sortDir} onClick={toggleSort} align="right" />
                  <th style={{ ...tdStyle, fontWeight: 700, color: 'var(--text-muted)', fontSize: '0.78rem' }}>Confidence</th>
                  <SortHeader label="Obs" sortKey="n_eligible_obs" activeKey={sortKey} dir={sortDir} onClick={toggleSort} align="right" />
                  <SortHeader label="Drill obs" sortKey="n_maxspeed_obs" activeKey={sortKey} dir={sortDir} onClick={toggleSort} align="right" />
                  <SortHeader label="Days since stimulus" sortKey="days_since_last_maxspeed" activeKey={sortKey} dir={sortDir} onClick={toggleSort} align="right" />
                  <th style={{ ...tdStyle, fontWeight: 700, color: 'var(--text-muted)', fontSize: '0.78rem' }}>Flags</th>
                </tr>
              </thead>
              <tbody>
                {sortedSquad.map(r => (
                  <tr key={r.player_id} onClick={() => { setPlayerId(String(r.player_id)); setView('player'); }}
                    style={{ cursor: 'pointer' }}>
                    <td style={{ ...tdStyle, color: 'var(--text-primary)', fontWeight: 600 }}>{r.player_name}</td>
                    <td style={tdStyle}>{r.position || '—'}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--text-primary)', fontWeight: 700 }}>{fmt(r.mss_ceiling_kmh)}</td>
                    <td style={tdStyle}><ConfidenceBadge confidence={r.confidence} nObs={r.n_eligible_obs} /></td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{r.n_eligible_obs}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{r.n_maxspeed_obs}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{r.days_since_last_maxspeed ?? '—'}</td>
                    <td style={tdStyle}>
                      <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
                        {r.provisional_mss_kmh != null && (
                          <span title="A new high awaiting a second observation within TE before it is promoted"
                            style={{ background: '#92400e20', color: '#f59e0b', borderRadius: 5, padding: '2px 7px', fontSize: '0.7rem', fontWeight: 700 }}>
                            <Clock size={9} style={{ verticalAlign: -1 }} /> {fmt(r.provisional_mss_kmh)} awaiting corroboration
                          </span>
                        )}
                        {r.status === 'declining_review' && (
                          <span style={{ background: '#7f1d1d20', color: '#ef4444', borderRadius: 5, padding: '2px 7px', fontSize: '0.7rem', fontWeight: 700 }}>Declining — review</span>
                        )}
                        {r.open_review_items > 0 && (
                          <span style={{ background: '#1e3a8a20', color: '#60a5fa', borderRadius: 5, padding: '2px 7px', fontSize: '0.7rem', fontWeight: 700 }}>{r.open_review_items} to review</span>
                        )}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ══════════════ PLAYER ══════════════ */}
      {view === 'player' && !loading && (
        <PlayerCard
          players={squad} playerId={playerId} setPlayerId={setPlayerId}
          detail={detail} history={history} />
      )}

      {/* ══════════════ REVIEW QUEUE ══════════════ */}
      {view === 'review' && !loading && (
        <ReviewQueue queue={queue} user={user} onDecided={reload} />
      )}

      {/* ══════════════ DRILL CLASSES ══════════════ */}
      {view === 'classes' && !loading && (
        <DrillClasses rows={classes} user={user} onSaved={reload} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function PlayerCard({ players, playerId, setPlayerId, detail, history }) {
  const mss = detail && detail.mss;
  const obs = (detail && detail.contributing_observations) || [];
  const series = (history && history.session_vmax) || [];
  const changePoints = (history && history.history) || [];

  const te = mss && mss.te_expression_kmh != null ? Number(mss.te_expression_kmh) : null;
  const swc = mss && mss.swc_kmh != null ? Number(mss.swc_kmh) : null;
  // The rolling 3-session mean has standard error TE / sqrt(3); the band drawn is
  // +/- 2 * TE / sqrt(3), the smallest movement that would count as real.
  const band = te != null ? (2 * te) / Math.sqrt(3) : null;
  const centre = series.length ? series.reduce((s, x) => s + x.sessionVmaxKmh, 0) / series.length : null;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <select value={playerId} onChange={e => setPlayerId(e.target.value)}
        style={{ padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-input, transparent)', color: 'var(--text-primary)', fontSize: '0.85rem', maxWidth: 320 }}>
        {players.map(p => <option key={p.player_id} value={String(p.player_id)}>{p.player_name}</option>)}
      </select>

      {!mss && <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>No MSS computed for this player yet.</p>}

      {mss && (
        <>
          <div style={{ ...cardStyle, display: 'grid', gap: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <MssValue value={mss.mss_ceiling_kmh} confidence={mss.confidence} nObs={mss.n_eligible_obs} size="1.6rem" />
              {mss.provisional_mss_kmh != null && (
                <span style={{ background: '#92400e20', color: '#f59e0b', borderRadius: 6, padding: '4px 10px', fontSize: '0.76rem', fontWeight: 700 }}>
                  {fmt(mss.provisional_mss_kmh)} km/h awaiting corroboration
                </span>
              )}
              {mss.status === 'declining_review' && (
                <span style={{ background: '#7f1d1d20', color: '#ef4444', borderRadius: 6, padding: '4px 10px', fontSize: '0.76rem', fontWeight: 700 }}>Declining — under review</span>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              <Stat label="Window" value={`${mss.window_weeks} weeks`} />
              <Stat label="Eligible observations" value={mss.n_eligible_obs} />
              <Stat label="From the max-speed drill" value={mss.n_maxspeed_obs} />
              <Stat label="From matches" value={mss.n_match_obs} />
              <Stat label="Days since stimulus" value={mss.days_since_last_maxspeed ?? '—'} />
              <Stat label="Ceiling came from" value={mss.source_class || '—'} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, fontSize: '0.8rem' }}>
              <Stat label={mss.te_is_default ? 'TE (default, not measured)' : 'TE (unstandardised protocol)'}
                value={te == null ? '—' : `${fmt(te)} km/h`} />
              <Stat label="Smallest worthwhile change" value={swc == null ? '—' : `${fmt(swc)} km/h`} />
            </div>
            {te != null && swc != null && te > swc && (
              <Note>
                <strong>TE ({fmt(te)}) is larger than the smallest worthwhile change ({fmt(swc)}).</strong> That is
                precisely why a single observation cannot be trusted and why a change is only
                declared once the rolling mean moves by more than {fmt(band)} km/h and holds.
                This TE is <em>expression</em> variability, not device error — it contains
                session-to-session readiness, and because the max-speed drill is prescribed at
                95% while most players attack it, it also contains how hard the player chose to
                go that week.
              </Note>
            )}
          </div>

          <div style={cardStyle}>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 4px' }}>Session Vmax trend</h3>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '0 0 14px' }}>
              Max-speed sessions only. Shown as a scatter with the ±2·TE/√3 noise band, and
              deliberately without trend arrows: a low week may mean the player did not attempt a
              maximum, not that he got slower. With a single-trial weekly value only changes of
              roughly 4% are detectable at all.
            </p>
            {series.length === 0 ? (
              <p style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>No max-speed sessions in range.</p>
            ) : (
              <div style={{ height: 260 }}>
                <ResponsiveContainer>
                  <ScatterChart margin={{ top: 8, right: 12, bottom: 8, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                    <YAxis dataKey="sessionVmaxKmh" domain={['dataMin - 1.5', 'dataMax + 1.5']}
                      tick={{ fontSize: 11, fill: 'var(--text-muted)' }} unit=" km/h" width={70} />
                    <Tooltip formatter={(v) => `${fmt(v)} km/h`} />
                    {centre != null && band != null && (
                      <ReferenceArea y1={centre - band} y2={centre + band} fill="#2563eb" fillOpacity={0.10} />
                    )}
                    {changePoints.filter(c => c.valid_from).map(c => (
                      <ReferenceLine key={c.id} x={String(c.valid_from).slice(0, 10)} stroke="#f59e0b"
                        strokeDasharray="4 3" label={{ value: 'MSS change', fontSize: 9, fill: '#f59e0b' }} />
                    ))}
                    <Scatter data={series.map(s => ({ ...s, date: String(s.date).slice(0, 10) }))} fill="#2563eb" />
                  </ScatterChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div style={{ ...cardStyle, padding: 0, overflowX: 'auto' }}>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0, padding: '16px 18px 8px' }}>Contributing observations</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
              <thead>
                <tr>{['Date', 'Drill', 'Class', 'Max speed', 'Minutes', 'Distance'].map(h => (
                  <th key={h} style={{ ...tdStyle, fontWeight: 700, color: 'var(--text-muted)', fontSize: '0.76rem' }}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {obs.map(o => (
                  <tr key={o.id}>
                    <td style={tdStyle}>{String(o.session_date).slice(0, 10)}</td>
                    <td style={tdStyle}>{o.drill_title}</td>
                    <td style={tdStyle}>{o.drill_class}</td>
                    <td style={{ ...tdStyle, fontWeight: 700, color: 'var(--text-primary)' }}>{fmt(o.max_speed_kmh)}</td>
                    <td style={tdStyle}>{fmt(o.minutes)}</td>
                    <td style={tdStyle}>{fmt(o.distance_m, 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)' }}>{value}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function ReviewQueue({ queue, user, onDecided }) {
  const [busy, setBusy] = useState(null);
  const [draft, setDraft] = useState({});
  const open = queue.filter(q => !q.decision);

  const decide = async (item, decision) => {
    const d = draft[item.id] || {};
    if (decision === 'reject' && !d.reason) { alert('Choose a rejection reason.'); return; }
    setBusy(item.id);
    try {
      await decideMssReview(item.id, {
        decision, reject_reason: d.reason, note: d.note, video_ref: d.video,
        user_id: user && user.id,
      });
      await onDecided();
    } catch (e) { alert(`Failed: ${e.message}`); }
    setBusy(null);
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Note tone="warn">
        <strong>What this screen can and cannot catch.</strong> The raw 10 Hz velocity trace is not
        available in this pipeline — Sonra exports arrive as per-drill summaries — so the
        gold-standard check (inspecting speed against acceleration, requiring the peak to be
        sustained) is impossible. What runs instead is a distributional screen against the
        player's own recent values. It catches large isolated spikes. It will <strong>not</strong> catch
        a moderate error of around +1 km/h. The export also carries no satellite count, HDOP or
        device identifier, so no signal-quality or hardware check is being performed at all.
      </Note>

      {open.length === 0 && <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Nothing awaiting review.</p>}

      {open.map(item => {
        const refs = Array.isArray(item.reference_values) ? item.reference_values : [];
        const d = draft[item.id] || {};
        const set = (k, v) => setDraft(s => ({ ...s, [item.id]: { ...s[item.id], [k]: v } }));
        return (
          <div key={item.id} style={{ ...cardStyle, display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)' }}>{item.player_name}</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                  {String(item.session_date).slice(0, 10)} · {item.session_title} · {item.drill_title} ({item.drill_class})
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '1.3rem', fontWeight: 800, color: '#f59e0b' }}>{fmt(item.max_speed_kmh)} km/h</div>
                <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>incumbent {fmt(item.mss_ceiling_kmh)} km/h</div>
              </div>
            </div>

            {/* ONE reason per item — the gap screen and the corroboration rule both catch a
                lone high value, and it must never show as two entries. */}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: '0.78rem', color: '#f59e0b', fontWeight: 600 }}>
              <AlertTriangle size={13} /> {FLAG_LABEL[item.flag_reason] || item.flag_reason}
              {item.gap_kmh != null && <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>· {fmt(item.gap_kmh)} km/h above the reference set</span>}
            </div>

            {refs.length > 0 && (
              <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)' }}>
                Reference values: {refs.map(v => fmt(v)).join(' · ')} km/h
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <select value={d.reason || ''} onChange={e => set('reason', e.target.value)}
                style={{ padding: '7px 10px', borderRadius: 7, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-primary)', fontSize: '0.8rem' }}>
                <option value="">Rejection reason…</option>
                {REJECT_REASONS.map(r => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
              </select>
              <input placeholder="Note" value={d.note || ''} onChange={e => set('note', e.target.value)}
                style={{ flex: '1 1 200px', padding: '7px 10px', borderRadius: 7, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-primary)', fontSize: '0.8rem' }} />
              <input placeholder="Video link" value={d.video || ''} onChange={e => set('video', e.target.value)}
                style={{ flex: '1 1 160px', padding: '7px 10px', borderRadius: 7, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-primary)', fontSize: '0.8rem' }} />
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <ActionButton disabled={busy === item.id} onClick={() => decide(item, 'confirm')} colour="#22c55e" icon={Check} label="Confirm" />
              <ActionButton disabled={busy === item.id} onClick={() => decide(item, 'reject')} colour="#ef4444" icon={X} label="Reject" />
              <ActionButton disabled={busy === item.id} onClick={() => decide(item, 'defer')} colour="#9ca3af" icon={Clock} label="Defer" />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ActionButton({ onClick, colour, icon: Icon, label, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: `1px solid ${colour}55`, background: `${colour}18`, color: colour, fontSize: '0.8rem', fontWeight: 700, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1, fontFamily: 'inherit' }}>
      <Icon size={13} /> {label}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function DrillClasses({ rows, user, onSaved }) {
  const [busy, setBusy] = useState(null);

  const save = async (title, cls) => {
    setBusy(title);
    try { await setMssDrillClass({ drill_title: title, class: cls, user_id: user && user.id }); await onSaved(); }
    catch (e) { alert(`Failed: ${e.message}`); }
    setBusy(null);
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Note>
        <strong>Classify from the evidence, not the name.</strong> Each row shows how that drill's
        values land as a percentage of the player's own ceiling. A format that regularly produces
        values within 5% of players' ceilings belongs in ELIGIBLE or MAXSPEED whatever its name
        suggests; one that never exceeds about 80% does not. Note the mild circularity — the
        ceiling is derived from the classification and the classification is informed by the
        ceiling. That is acceptable because you make the final call and the effect is
        one-directional: a wrongly-included drill can only raise a ceiling, which the plausibility
        screen and review queue then catch. This is never automated.
        <br /><br />
        <strong>SESSION_ONLY</strong> marks whole-session rows, whose speed carries no drill
        context. They count only for sessions with no drill breakdown at all — which is what keeps
        history before drill-level logging usable without letting a rondo spike back in.
      </Note>

      <div style={{ ...cardStyle, padding: 0, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 820 }}>
          <thead>
            <tr>{['Drill', 'Obs', 'Players', 'Median % of ceiling', 'p90 %', 'Within 5%', 'Class'].map(h => (
              <th key={h} style={{ ...tdStyle, fontWeight: 700, color: 'var(--text-muted)', fontSize: '0.76rem' }}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.drill_title}>
                <td style={{ ...tdStyle, color: 'var(--text-primary)', fontWeight: 600 }}>{r.drill_title}</td>
                <td style={tdStyle}>{r.n_obs}</td>
                <td style={tdStyle}>{r.n_players}</td>
                <td style={{ ...tdStyle, fontWeight: 700, color: Number(r.median_pct) >= 80 ? '#22c55e' : 'var(--text-secondary)' }}>{r.median_pct ?? '—'}%</td>
                <td style={tdStyle}>{r.p90_pct ?? '—'}%</td>
                <td style={tdStyle}>{r.within5_pct ?? '—'}%</td>
                <td style={tdStyle}>
                  <select value={r.class} disabled={busy === r.drill_title}
                    onChange={e => save(r.drill_title, e.target.value)}
                    style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border-strong)', background: 'transparent', color: r.class === 'UNCLASSIFIED' ? '#f59e0b' : 'var(--text-primary)', fontSize: '0.78rem' }}>
                    {CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
